import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    type BarDuration,
    type ChartPatternAuditArtifact,
    type ChartPatternAuditBatchSummary,
    type ChartPatternAuditItemDetail,
    type ChartPatternAuditQueueItem,
    type ChartPatternAuditReasonCode,
    type ChartPatternDirection,
    type ChartPatternKey as ChartPatternKeyType,
    type ChartPatternState as ChartPatternStateType,
    ChartPatternAuditReviewerType,
    ChartPatternAuditVerdict as ChartPatternAuditVerdictEnum
} from '@einhaus/project314-types';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { Badge, Button, FormCheckbox, FormSelect } from '@components/UI';
import {
    type ReviewFormState,
    buildArtifactImageUrl,
    buildAssetChartLink,
    buildChartAuditLink,
    confidenceFromNumeric,
    emptyReviewForm,
    formatPercent,
    formatPrice,
    getIssueCodeOptions,
    issueLabels,
    patternKeyLabels,
    patternOptions,
    renderStatusLabels,
    statusOptions,
    timeframeLabel,
    timframeOptions,
    verdictOptions
} from './chartPatternAuditConstants';

type ArtifactAnnotation = 'annotated' | 'raw';
type ArtifactZoom = 'focused' | 'wide';
type OverlapSnapshot = ChartPatternAuditItemDetail['overlaps'][number];

type ChartPatternAuditReviewTopBarProps = {
    batch: ChartPatternAuditBatchSummary | null;
    batchId: number;
    isBatchComplete: boolean;
    nextIncompleteBatch: ChartPatternAuditBatchSummary | null;
    onBack: () => void;
    onOpenNextAudit: () => void;
    onToggleShortcutHelp: () => void;
    progressPct: number;
    reviewedCount: number;
    totalCount: number;
};

type ChartPatternAuditShortcutHelpBannerProps = {
    showShortcutHelp: boolean;
};

type ChartPatternAuditReviewSidebarProps = {
    currentIndex: number;
    filterPattern: ChartPatternKeyType | '';
    filterStatus: ChartPatternStateType | '';
    filterTimeframe: BarDuration | '';
    filterUnreviewed: boolean;
    isCollapsed: boolean;
    items: ChartPatternAuditQueueItem[];
    onFilterPatternChange: (value: ChartPatternKeyType | '') => void;
    onFilterStatusChange: (value: ChartPatternStateType | '') => void;
    onFilterTimeframeChange: (value: BarDuration | '') => void;
    onFilterUnreviewedChange: (checked: boolean) => void;
    onSelectItem: (itemId: number) => void;
    onToggleCollapsed: () => void;
    selectedItemId: number | null;
};

type ChartPatternAuditReviewCenterPanelProps = {
    artifactLoadFailed: boolean;
    currentArtifact: ChartPatternAuditArtifact | null;
    detail: ChartPatternAuditItemDetail | null;
    detailIsLoading: boolean;
    imageAnnotation: ArtifactAnnotation;
    imageZoom: ArtifactZoom;
    isRunningAiReview: boolean;
    isRenderingArtifacts: boolean;
    onArtifactLoadError: (artifactId: number) => void;
    onOpenFullscreen: () => void;
    onRenderArtifacts: () => void;
    onRunAiReview: () => void;
    onSetImageAnnotation: (annotation: ArtifactAnnotation) => void;
    onSetImageZoom: (zoom: ArtifactZoom) => void;
};

type ChartPatternAuditReviewInspectorPanelProps = {
    currentIndex: number;
    currentOccurrenceSnapshot: OverlapSnapshot | null;
    detail: ChartPatternAuditItemDetail;
    isBatchComplete: boolean;
    isSaving: boolean;
    itemsLength: number;
    nextIncompleteBatch: ChartPatternAuditBatchSummary | null;
    onGoNext: () => void;
    onGoPrev: () => void;
    onOpenNextAudit: () => void;
    onSaveAndNext: (reviewForm: ReviewFormState) => Promise<void>;
    onSaveReview: (reviewForm: ReviewFormState) => Promise<void>;
    onToggleShortcutHelp: () => void;
};

type ChartPatternAuditFullscreenModalProps = {
    artifactLoadFailed: boolean;
    currentArtifact: ChartPatternAuditArtifact | null;
    imageFullscreen: boolean;
    onClose: () => void;
};

type ReviewFormSectionProps = {
    issueCodeOptions: ChartPatternAuditReasonCode[];
    onToggleReasonCode: (code: ChartPatternAuditReasonCode) => void;
    reviewForm: ReviewFormState;
    setReviewForm: Dispatch<SetStateAction<ReviewFormState>>;
};

type PatternDetailsSectionProps = {
    currentOccurrenceSnapshot: OverlapSnapshot | null;
    detail: ChartPatternAuditItemDetail;
};

type WeeklyTimingSectionProps = {
    weeklyTimingPoints: ChartPatternAuditItemDetail['weeklyTimingPoints'];
};

type AdvancedDetailsSectionProps = {
    detail: ChartPatternAuditItemDetail;
    onToggle: () => void;
    showAdvanced: boolean;
};

type ReviewFooterProps = {
    currentIndex: number;
    isBatchComplete: boolean;
    isSaving: boolean;
    itemsLength: number;
    nextIncompleteBatch: ChartPatternAuditBatchSummary | null;
    onGoNext: () => void;
    onGoPrev: () => void;
    onOpenNextAudit: () => void;
    onSaveAndNext: () => Promise<void>;
    onSaveReview: () => Promise<void>;
    reviewForm: ReviewFormState;
};

const verdictButtonStyles: Record<string, { active: string; inactive: string; key: string }> = {
    green: {
        active: 'border-green-500 bg-green-500/20 text-green-300 ring-1 ring-green-500/40',
        inactive: 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-green-600 hover:text-green-400',
        key: '1'
    },
    yellow: {
        active: 'border-yellow-500 bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/40',
        inactive: 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-yellow-600 hover:text-yellow-400',
        key: '2'
    },
    red: {
        active: 'border-red-500 bg-red-500/20 text-red-300 ring-1 ring-red-500/40',
        inactive: 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-red-600 hover:text-red-400',
        key: '3'
    },
    orange: {
        active: 'border-orange-500 bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/40',
        inactive: 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-orange-600 hover:text-orange-400',
        key: '4'
    },
    gray: {
        active: 'border-zinc-400 bg-zinc-400/20 text-zinc-200 ring-1 ring-zinc-400/40',
        inactive: 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500 hover:text-zinc-300',
        key: '5'
    }
};

const verdictBadge = (verdict: string | null): { className: string; text: string } | null => {
    if (!verdict) return null;

    const map: Record<string, { className: string; text: string }> = {
        [ChartPatternAuditVerdictEnum.correct]: { className: 'text-green-400', text: 'Valid' },
        [ChartPatternAuditVerdictEnum.weakButAcceptable]: { className: 'text-yellow-400', text: 'Bord.' },
        [ChartPatternAuditVerdictEnum.incorrect]: { className: 'text-red-400', text: 'Invalid' },
        [ChartPatternAuditVerdictEnum.duplicate]: { className: 'text-orange-400', text: 'Dup.' },
        [ChartPatternAuditVerdictEnum.uncertain]: { className: 'text-zinc-400', text: 'Skip' }
    };

    return map[verdict] ?? null;
};

const getAuditConfirmationLabel = (direction: ChartPatternDirection): string => {
    return direction === 'bearish' ? 'Breakdown' : 'Breakout';
};

const formatAuditDateTime = (dateTime: string | null | undefined): string => {
    if (!dateTime) {
        return '—';
    }

    return dateTime.slice(0, 10);
};

const buildReviewForm = (detail: ChartPatternAuditItemDetail): ReviewFormState => {
    const latestHuman = detail.reviews.find((review) => review.reviewerType === ChartPatternAuditReviewerType.human) ?? null;

    return {
        verdict: latestHuman?.verdict ?? emptyReviewForm.verdict,
        confidence:
            latestHuman?.confidence !== null && latestHuman?.confidence !== undefined ? confidenceFromNumeric(latestHuman.confidence) : '',
        reasonCodes: latestHuman?.reasonCodes ?? [],
        notes: latestHuman?.notes ?? ''
    };
};

const getArtifactPlaceholderMessage = (detail: ChartPatternAuditItemDetail, artifactLoadFailed: boolean): string => {
    if (artifactLoadFailed) {
        return 'Stored chart image is missing. Click "Generate Charts" to regenerate it.';
    }

    if (detail.item.renderStatus === 'pending') {
        return 'Charts not generated yet. Click "Generate Charts" above.';
    }

    if (detail.item.renderStatus === 'rendering') {
        return 'Charts are being generated…';
    }

    if (detail.item.renderStatus === 'failed') {
        return 'Chart generation failed.';
    }

    return 'This view is not available.';
};

const ReviewFormSection = ({ issueCodeOptions, onToggleReasonCode, reviewForm, setReviewForm }: ReviewFormSectionProps) => {
    return (
        <>
            <div>
                <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Verdict</div>
                <div className="grid grid-cols-2 gap-1.5">
                    {verdictOptions.map((option) => {
                        const isActive = reviewForm.verdict === option.value;
                        const styles = verdictButtonStyles[option.color];

                        return (
                            <button
                                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${isActive ? styles.active : styles.inactive}`}
                                key={option.value}
                                type="button"
                                onClick={() => setReviewForm((current) => ({ ...current, verdict: option.value }))}
                            >
                                {option.label}
                                <span className="ml-1 text-[10px] opacity-50">{styles.key}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Confidence</div>
                <div className="flex gap-1.5">
                    {(['low', 'medium', 'high'] as const).map((level) => (
                        <button
                            className={`flex-1 px-2 py-1.5 rounded border text-xs font-medium transition-colors ${
                                reviewForm.confidence === level
                                    ? 'border-p314-blue bg-p314-blue/20 text-blue-300'
                                    : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500'
                            }`}
                            key={level}
                            type="button"
                            onClick={() => setReviewForm((current) => ({ ...current, confidence: level }))}
                        >
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Issues</div>
                <div className="flex flex-wrap gap-1">
                    {issueCodeOptions.map((code) => {
                        const isSelected = reviewForm.reasonCodes.includes(code);

                        return (
                            <button
                                className={`px-2 py-0.5 rounded-full border text-[11px] transition-colors ${
                                    isSelected
                                        ? 'border-red-600 bg-red-500/15 text-red-300'
                                        : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500'
                                }`}
                                key={code}
                                type="button"
                                onClick={() => onToggleReasonCode(code)}
                            >
                                {issueLabels[code]}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1" htmlFor="reviewNotes">
                    Notes
                </label>
                <textarea
                    className="w-full min-h-[48px] rounded-md bg-[#1E1E1E] border border-[#444] px-2.5 py-1.5 text-sm text-white focus:border-[#888] focus:outline-none resize-y"
                    id="reviewNotes"
                    placeholder="Optional notes…"
                    value={reviewForm.notes}
                    onChange={(event) => setReviewForm((current) => ({ ...current, notes: event.target.value }))}
                />
            </div>
        </>
    );
};

const PatternDetailsSection = ({ currentOccurrenceSnapshot, detail }: PatternDetailsSectionProps) => {
    return (
        <div>
            <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Pattern Details</div>
            <div className="space-y-1.5 text-xs">
                <div className="flex justify-between gap-2">
                    <span className="text-zinc-500">Quality</span>
                    <span className="text-white font-medium">{formatPrice(detail.item.qualityScore)}</span>
                </div>
                <div className="flex justify-between gap-2">
                    <span className="text-zinc-500">Similar</span>
                    <span className="text-white">{detail.item.overlapCount}</span>
                </div>
                <div className="flex justify-between gap-2">
                    <span className="text-zinc-500">{`Dist. to ${getAuditConfirmationLabel(detail.item.direction)}`}</span>
                    <span className="text-white">{formatPercent(currentOccurrenceSnapshot?.breakoutDistancePct)}</span>
                </div>
                <div className="h-px bg-zinc-800/50 my-0.5" />
                <div className="flex justify-between gap-2">
                    <span className="text-zinc-500">Resistance</span>
                    <span className="text-white">{formatPrice(detail.occurrence.resistancePrice)}</span>
                </div>
                <div className="flex justify-between gap-2">
                    <span className="text-zinc-500">{getAuditConfirmationLabel(detail.item.direction)}</span>
                    <span className="text-white">{formatPrice(detail.occurrence.breakoutPrice)}</span>
                </div>
                <div className="flex justify-between gap-2">
                    <span className="text-zinc-500">Stop</span>
                    <span className="text-white">{formatPrice(detail.occurrence.stopPrice)}</span>
                </div>
                <div className="flex justify-between gap-2">
                    <span className="text-zinc-500">Target 1</span>
                    <span className="text-white">{formatPrice(detail.occurrence.target1Price)}</span>
                </div>
                <div className="flex justify-between gap-2">
                    <span className="text-zinc-500">Target 2</span>
                    <span className="text-white">{formatPrice(detail.occurrence.target2Price)}</span>
                </div>
            </div>
        </div>
    );
};

const WeeklyTimingSection = ({ weeklyTimingPoints }: WeeklyTimingSectionProps) => {
    if (!weeklyTimingPoints.length) {
        return null;
    }

    return (
        <div>
            <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Weekly Timing</div>
            <div className="space-y-2 text-xs">
                {weeklyTimingPoints.map((point) => (
                    <div
                        className="rounded border border-zinc-800 bg-black/20 px-2 py-1.5"
                        key={`${point.key}-${point.weekEndingDateTime}`}
                    >
                        <div className="font-medium text-zinc-200">{point.label}</div>
                        <div className="mt-0.5 text-zinc-500">Week ending {formatAuditDateTime(point.weekEndingDateTime)}</div>
                        <div className="text-zinc-400">
                            {point.exactLabel ? `${point.exactLabel}: ` : 'exact daily date: '}
                            {formatAuditDateTime(point.exactDateTime)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AiReviewSummary = ({ detail }: { detail: ChartPatternAuditItemDetail }) => {
    const aiReview = detail.reviews.find((review) => review.reviewerType === ChartPatternAuditReviewerType.ai) ?? null;

    if (!aiReview) {
        return null;
    }

    const aiBadge = verdictBadge(aiReview.verdict);

    return (
        <>
            <div className="h-px bg-zinc-800" />
            <div>
                <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1">AI Verdict</div>
                <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${aiBadge?.className ?? 'text-zinc-300'}`}>
                        {aiBadge?.text ?? aiReview.verdict}
                    </span>
                    {aiReview.notes ? (
                        <span className="text-xs text-zinc-500 truncate" title={aiReview.notes}>
                            {aiReview.notes}
                        </span>
                    ) : null}
                </div>
            </div>
        </>
    );
};

const AdvancedDetailsSection = ({ detail, onToggle, showAdvanced }: AdvancedDetailsSectionProps) => {
    return (
        <div className="border-t border-zinc-800 pt-2">
            <button
                className="w-full flex items-center justify-between py-1 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider hover:text-zinc-300 transition-colors"
                type="button"
                onClick={onToggle}
            >
                <span>Advanced</span>
                <svg
                    className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                </svg>
            </button>

            {showAdvanced ? (
                <div className="mt-2 space-y-3">
                    <div>
                        <div className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-1">Key Points</div>
                        <div className="space-y-1 text-xs">
                            {detail.occurrence.anchors.map((anchor) => (
                                <div className="flex justify-between gap-2" key={`${anchor.role}-${anchor.timestamp}`}>
                                    <span className="text-zinc-500">{anchor.role}</span>
                                    <span className="text-white text-[11px]">
                                        {anchor.dateTime} @ {formatPrice(anchor.price)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {detail.overlaps.length > 0 ? (
                        <div>
                            <div className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-1">
                                Similar ({detail.overlaps.length})
                            </div>
                            <div className="space-y-1 text-xs">
                                {detail.overlaps.map((overlap) => (
                                    <div
                                        className="flex items-center justify-between gap-2 rounded border border-zinc-800 bg-black/20 px-2 py-1"
                                        key={overlap.occurrenceKey}
                                    >
                                        <span className="text-zinc-300 truncate">
                                            {patternKeyLabels[overlap.patternKey] ?? overlap.patternKey}
                                        </span>
                                        <span className="text-[10px] text-zinc-500 shrink-0">
                                            {overlap.direction} · Q:{formatPrice(overlap.qualityScore)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {detail.reviews.length > 0 ? (
                        <div>
                            <div className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-1">History</div>
                            <div className="space-y-1.5">
                                {detail.reviews.map((review) => {
                                    const badge = verdictBadge(review.verdict);

                                    return (
                                        <div className="rounded border border-zinc-800 bg-black/20 px-2 py-1.5" key={review.id}>
                                            <div className="flex items-center justify-between gap-1 text-xs">
                                                <span className={`font-medium ${badge?.className ?? 'text-zinc-300'}`}>
                                                    {badge?.text ?? review.verdict}
                                                </span>
                                                <span className="text-[10px] text-zinc-500">
                                                    {review.reviewerType === 'human'
                                                        ? (review.reviewerEmail ?? 'Unknown')
                                                        : `AI (${review.reviewerModelKey ?? 'auto'})`}
                                                </span>
                                            </div>
                                            {review.reasonCodes.length > 0 ? (
                                                <div className="mt-1 flex flex-wrap gap-0.5">
                                                    {review.reasonCodes.map((code) => (
                                                        <span
                                                            className="rounded-full border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-[9px] text-zinc-400"
                                                            key={`${review.id}-${code}`}
                                                        >
                                                            {issueLabels[code]}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : null}
                                            {review.notes ? (
                                                <div className="mt-0.5 text-[10px] text-zinc-500 truncate" title={review.notes}>
                                                    {review.notes}
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    <div>
                        <div className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wider mb-1">Detector</div>
                        <div className="text-[10px] text-zinc-500">v{detail.occurrence.detectorVersion}</div>
                        {detail.occurrence.metrics && Object.keys(detail.occurrence.metrics).length > 0 ? (
                            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap text-[10px] text-zinc-500 bg-black/30 rounded p-1.5">
                                {JSON.stringify(detail.occurrence.metrics, null, 2)}
                            </pre>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const ReviewFooter = ({
    currentIndex,
    isBatchComplete,
    isSaving,
    itemsLength,
    nextIncompleteBatch,
    onGoNext,
    onGoPrev,
    onOpenNextAudit,
    onSaveAndNext,
    onSaveReview,
    reviewForm
}: ReviewFooterProps) => {
    return (
        <div className="shrink-0 border-t border-zinc-800 p-3 space-y-2 bg-[#1a1a1a]">
            {isBatchComplete ? (
                <div className="flex items-center justify-between rounded border border-green-900/60 bg-green-950/20 px-3 py-2 text-xs text-green-200">
                    <span>
                        Batch complete.
                        {nextIncompleteBatch
                            ? ` Next up: #${nextIncompleteBatch.id} ${nextIncompleteBatch.name}`
                            : ' No more incomplete audits.'}
                    </span>
                    {nextIncompleteBatch ? (
                        <Button size="sm" variant="secondary" onClick={onOpenNextAudit}>
                            Next Audit
                        </Button>
                    ) : null}
                </div>
            ) : null}
            <div className="flex gap-2">
                <Button
                    className="flex-1"
                    disabled={!reviewForm.verdict}
                    isLoading={isSaving}
                    variant="primary"
                    onClick={() => onSaveAndNext()}
                >
                    Save & Next
                    <kbd className="ml-1.5 text-[10px] opacity-60 font-mono">⌘↵</kbd>
                </Button>
                <Button disabled={!reviewForm.verdict} isLoading={isSaving} size="sm" variant="secondary" onClick={() => onSaveReview()}>
                    Save
                </Button>
            </div>
            <div className="flex items-center justify-between">
                <Button disabled={currentIndex <= 0} size="sm" variant="dark" onClick={onGoPrev}>
                    ←
                </Button>
                <span className="text-[11px] text-zinc-500">
                    {currentIndex + 1} / {itemsLength}
                </span>
                <Button disabled={currentIndex >= itemsLength - 1} size="sm" variant="dark" onClick={onGoNext}>
                    →
                </Button>
            </div>
        </div>
    );
};

export const ChartPatternAuditReviewTopBar = ({
    batch,
    batchId,
    isBatchComplete,
    nextIncompleteBatch,
    onBack,
    onOpenNextAudit,
    onToggleShortcutHelp,
    progressPct,
    reviewedCount,
    totalCount
}: ChartPatternAuditReviewTopBarProps) => {
    return (
        <div className="shrink-0 flex items-center gap-4 px-4 py-2.5 border-b border-zinc-800 bg-[#1a1a1a]">
            <button
                className="text-sm text-zinc-400 hover:text-white transition-colors flex items-center gap-1"
                type="button"
                onClick={onBack}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                </svg>
                Batches
            </button>

            <div className="h-4 w-px bg-zinc-700" />

            <div className="flex min-w-0 items-center gap-2">
                <div className="rounded bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-400 shrink-0">#{batchId}</div>
                <div className="text-sm text-white font-medium truncate">{batch?.name ?? `Batch #${batchId}`}</div>
            </div>

            {batch ? (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                    <span>{patternKeyLabels[batch.patternKey ?? ''] ?? 'All'}</span>
                    <span className="text-zinc-600">·</span>
                    <span>{timeframeLabel(batch.barDuration)}</span>
                </div>
            ) : null}

            <div className="ml-auto flex items-center gap-3">
                {isBatchComplete ? (
                    <div className="rounded-full border border-green-800 bg-green-950/40 px-2.5 py-1 text-[11px] font-medium text-green-300">
                        Audit Complete
                    </div>
                ) : null}
                <div className="text-xs text-zinc-400">
                    {reviewedCount}/{totalCount} reviewed
                </div>
                <div className="w-32 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                    <div className="h-full rounded-full bg-p314-blue transition-all" style={{ width: `${progressPct}%` }} />
                </div>
                {isBatchComplete && nextIncompleteBatch ? (
                    <Button size="sm" variant="secondary" onClick={onOpenNextAudit}>
                        Next Audit
                    </Button>
                ) : null}
                <button
                    className="text-zinc-500 hover:text-zinc-300 text-xs border border-zinc-700 rounded px-1.5 py-0.5"
                    title="Keyboard shortcuts"
                    type="button"
                    onClick={onToggleShortcutHelp}
                >
                    ?
                </button>
            </div>
        </div>
    );
};

export const ChartPatternAuditShortcutHelpBanner = ({ showShortcutHelp }: ChartPatternAuditShortcutHelpBannerProps) => {
    if (!showShortcutHelp) {
        return null;
    }

    return (
        <div className="shrink-0 px-4 py-2 border-b border-zinc-800 bg-zinc-900/80 text-xs text-zinc-300 flex flex-wrap gap-x-6 gap-y-1">
            <span>
                <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-200 font-mono">1-5</kbd> Set verdict
            </span>
            <span>
                <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-200 font-mono">←</kbd>{' '}
                <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-200 font-mono">→</kbd> Navigate items
            </span>
            <span>
                <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-200 font-mono">⌘+Enter</kbd> Save & Next
            </span>
            <span>
                <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-200 font-mono">?</kbd> Toggle this help
            </span>
        </div>
    );
};

export const ChartPatternAuditReviewSidebar = ({
    currentIndex,
    filterPattern,
    filterStatus,
    filterTimeframe,
    filterUnreviewed,
    isCollapsed,
    items,
    onFilterPatternChange,
    onFilterStatusChange,
    onFilterTimeframeChange,
    onFilterUnreviewedChange,
    onSelectItem,
    onToggleCollapsed,
    selectedItemId
}: ChartPatternAuditReviewSidebarProps) => {
    return (
        <div
            className={`shrink-0 border-r border-zinc-800 flex flex-col bg-[#1a1a1a] transition-[width] duration-200 ${isCollapsed ? 'w-10' : 'w-56'}`}
        >
            <button
                className="shrink-0 flex items-center justify-center py-2 border-b border-zinc-800 text-zinc-500 hover:text-white transition-colors"
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                type="button"
                onClick={onToggleCollapsed}
            >
                <svg
                    className={`w-4 h-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path d="M11 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                </svg>
            </button>

            {isCollapsed ? null : (
                <>
                    <div className="p-2 border-b border-zinc-800 space-y-1.5">
                        <FormSelect
                            noMargin
                            options={patternOptions}
                            size="sm"
                            value={filterPattern}
                            onChange={(event) => onFilterPatternChange(event.target.value as ChartPatternKeyType | '')}
                        />
                        <div className="grid grid-cols-2 gap-1.5">
                            <FormSelect
                                noMargin
                                options={timframeOptions}
                                size="sm"
                                value={filterTimeframe}
                                onChange={(event) => onFilterTimeframeChange(event.target.value as BarDuration | '')}
                            />
                            <FormSelect
                                noMargin
                                options={statusOptions}
                                size="sm"
                                value={filterStatus}
                                onChange={(event) => onFilterStatusChange(event.target.value as ChartPatternStateType | '')}
                            />
                        </div>
                        <FormCheckbox
                            checked={filterUnreviewed}
                            label="Unreviewed only"
                            onChange={(event) => onFilterUnreviewedChange(event.target.checked)}
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="p-3 text-xs text-zinc-500">No patterns match these filters.</div>
                        ) : (
                            items.map((item, index) => {
                                const badge = verdictBadge(item.latestHumanVerdict);
                                const isActive = item.id === selectedItemId;

                                return (
                                    <button
                                        className={`w-full text-left px-3 py-2 border-b border-zinc-800/50 transition-colors ${
                                            isActive
                                                ? 'bg-zinc-800/80 border-l-2 border-l-p314-blue'
                                                : 'hover:bg-zinc-800/40 border-l-2 border-l-transparent'
                                        }`}
                                        key={item.id}
                                        type="button"
                                        onClick={() => onSelectItem(item.id)}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className={`text-sm font-medium ${isActive ? 'text-white' : 'text-zinc-300'}`}>
                                                {item.assetSymbol}
                                            </span>
                                            {badge ? (
                                                <span className={`text-[10px] font-medium ${badge.className}`}>{badge.text}</span>
                                            ) : (
                                                <span className="text-[10px] text-zinc-600">{index + 1}</span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-zinc-500 mt-0.5">
                                            Q: {formatPrice(item.qualityScore)}
                                            {item.overlapCount > 0 ? ` · ${item.overlapCount} similar` : ''}
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>

                    <div className="shrink-0 px-3 py-2 border-t border-zinc-800 text-[11px] text-zinc-500">
                        {items.length} pattern{items.length !== 1 ? 's' : ''}
                        {currentIndex >= 0 ? ` · Viewing ${currentIndex + 1}` : ''}
                    </div>
                </>
            )}
        </div>
    );
};

export const ChartPatternAuditReviewCenterPanel = ({
    artifactLoadFailed,
    currentArtifact,
    detail,
    detailIsLoading,
    imageAnnotation,
    imageZoom,
    isRunningAiReview,
    isRenderingArtifacts,
    onArtifactLoadError,
    onOpenFullscreen,
    onRenderArtifacts,
    onRunAiReview,
    onSetImageAnnotation,
    onSetImageZoom
}: ChartPatternAuditReviewCenterPanelProps) => {
    if (!detail) {
        return (
            <div className="flex-1 flex flex-col min-w-0 min-h-0">
                {detailIsLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <DataLoadingSpinner text="Loading pattern…" />
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-full text-zinc-500">
                        Select a pattern from the sidebar to begin reviewing.
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
            <div className="shrink-0 flex items-center justify-between gap-3 px-4 py-2 border-b border-zinc-800">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="text-base font-semibold text-white truncate">{detail.item.assetSymbol}</span>
                    {detail.item.assetName ? <span className="text-sm text-zinc-500 truncate">{detail.item.assetName}</span> : null}
                    <span className="text-xs text-zinc-500 shrink-0">
                        {patternKeyLabels[detail.item.patternKey] ?? detail.item.patternKey} · {timeframeLabel(detail.item.barDuration)} ·{' '}
                        {detail.item.state}
                    </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button isLoading={isRenderingArtifacts} size="sm" variant="secondary" onClick={onRenderArtifacts}>
                        Generate Charts
                    </Button>
                    <Button isLoading={isRunningAiReview} size="sm" variant="info" onClick={onRunAiReview}>
                        Run AI Review
                    </Button>
                    <Link className="text-blue-400 hover:text-blue-300 text-xs underline" target="_blank" to={buildChartAuditLink(detail)}>
                        Full Chart
                    </Link>
                    <Link className="text-blue-400 hover:text-blue-300 text-xs underline" target="_blank" to={buildAssetChartLink(detail)}>
                        Asset Page
                    </Link>
                </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col bg-zinc-950">
                <div className="shrink-0 flex items-center justify-between gap-3 px-3 py-1.5 border-b border-zinc-800">
                    <div className="flex items-center gap-1">
                        <button
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                                imageAnnotation === 'annotated' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
                            }`}
                            type="button"
                            onClick={() => onSetImageAnnotation('annotated')}
                        >
                            Annotated
                        </button>
                        <button
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                                imageAnnotation === 'raw' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
                            }`}
                            type="button"
                            onClick={() => onSetImageAnnotation('raw')}
                        >
                            Raw
                        </button>
                        <div className="w-px h-4 bg-zinc-700 mx-1" />
                        <button
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                                imageZoom === 'focused' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
                            }`}
                            type="button"
                            onClick={() => onSetImageZoom('focused')}
                        >
                            Focused
                        </button>
                        <button
                            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                                imageZoom === 'wide' ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'
                            }`}
                            type="button"
                            onClick={() => onSetImageZoom('wide')}
                        >
                            Wide
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <Badge
                            text={renderStatusLabels[detail.item.renderStatus]}
                            variant={detail.item.renderStatus === 'rendered' ? 'success' : 'secondary'}
                        />
                        {currentArtifact ? (
                            <button
                                className="text-xs text-zinc-400 hover:text-white"
                                title="Expand image"
                                type="button"
                                onClick={onOpenFullscreen}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                    />
                                </svg>
                            </button>
                        ) : null}
                    </div>
                </div>

                <div className="flex-1 min-h-0 relative bg-black flex items-center justify-center overflow-hidden">
                    {currentArtifact && !artifactLoadFailed ? (
                        <button className="max-w-full max-h-full cursor-pointer" type="button" onClick={onOpenFullscreen}>
                            <img
                                alt={`${imageAnnotation} ${imageZoom} chart`}
                                className="max-w-full max-h-full object-contain"
                                crossOrigin="use-credentials"
                                src={buildArtifactImageUrl(currentArtifact)}
                                onError={() => onArtifactLoadError(currentArtifact.id)}
                            />
                        </button>
                    ) : (
                        <div className="max-w-md text-center text-sm text-zinc-500 px-6">
                            {getArtifactPlaceholderMessage(detail, artifactLoadFailed)}
                        </div>
                    )}
                </div>

                {detail.item.renderErrorMessage ? (
                    <div className="shrink-0 px-3 py-2 border-t border-red-900/60 bg-red-950/20 text-sm text-red-200">
                        {detail.item.renderErrorMessage}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export const ChartPatternAuditReviewInspectorPanel = ({
    currentIndex,
    currentOccurrenceSnapshot,
    detail,
    isBatchComplete,
    isSaving,
    itemsLength,
    nextIncompleteBatch,
    onGoNext,
    onGoPrev,
    onOpenNextAudit,
    onSaveAndNext,
    onSaveReview,
    onToggleShortcutHelp
}: ChartPatternAuditReviewInspectorPanelProps) => {
    const [reviewForm, setReviewForm] = useState<ReviewFormState>(() => buildReviewForm(detail));
    const [showAdvanced, setShowAdvanced] = useState(false);
    const issueCodeOptions = getIssueCodeOptions(detail.item.patternKey);

    const toggleReasonCode = (code: ChartPatternAuditReasonCode) => {
        setReviewForm((current) => ({
            ...current,
            reasonCodes: current.reasonCodes.includes(code)
                ? current.reasonCodes.filter((reasonCode) => reasonCode !== code)
                : [...current.reasonCodes, code]
        }));
    };

    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            const tag = (event.target as HTMLElement).tagName;

            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
                return;
            }

            switch (event.key) {
                case '1':
                    setReviewForm((current) => ({ ...current, verdict: ChartPatternAuditVerdictEnum.correct }));
                    break;
                case '2':
                    setReviewForm((current) => ({ ...current, verdict: ChartPatternAuditVerdictEnum.weakButAcceptable }));
                    break;
                case '3':
                    setReviewForm((current) => ({ ...current, verdict: ChartPatternAuditVerdictEnum.incorrect }));
                    break;
                case '4':
                    setReviewForm((current) => ({ ...current, verdict: ChartPatternAuditVerdictEnum.duplicate }));
                    break;
                case '5':
                    setReviewForm((current) => ({ ...current, verdict: ChartPatternAuditVerdictEnum.uncertain }));
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    onGoPrev();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    onGoNext();
                    break;
                case 'Enter':
                    if (event.metaKey || event.ctrlKey) {
                        event.preventDefault();
                        void onSaveAndNext(reviewForm);
                    }

                    break;
                case '?':
                    onToggleShortcutHelp();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onGoNext, onGoPrev, onSaveAndNext, onToggleShortcutHelp, reviewForm]);

    return (
        <div className="w-72 shrink-0 border-l border-zinc-800 flex flex-col bg-[#1a1a1a]">
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
                <ReviewFormSection
                    issueCodeOptions={issueCodeOptions}
                    reviewForm={reviewForm}
                    setReviewForm={setReviewForm}
                    onToggleReasonCode={toggleReasonCode}
                />
                <div className="h-px bg-zinc-800" />
                <PatternDetailsSection currentOccurrenceSnapshot={currentOccurrenceSnapshot} detail={detail} />
                <WeeklyTimingSection weeklyTimingPoints={detail.weeklyTimingPoints} />
                <AiReviewSummary detail={detail} />
                <AdvancedDetailsSection
                    detail={detail}
                    showAdvanced={showAdvanced}
                    onToggle={() => setShowAdvanced((current) => !current)}
                />
            </div>

            <ReviewFooter
                currentIndex={currentIndex}
                isBatchComplete={isBatchComplete}
                isSaving={isSaving}
                itemsLength={itemsLength}
                nextIncompleteBatch={nextIncompleteBatch}
                reviewForm={reviewForm}
                onGoNext={onGoNext}
                onGoPrev={onGoPrev}
                onOpenNextAudit={onOpenNextAudit}
                onSaveAndNext={() => onSaveAndNext(reviewForm)}
                onSaveReview={() => onSaveReview(reviewForm)}
            />
        </div>
    );
};

export const ChartPatternAuditFullscreenModal = ({
    artifactLoadFailed,
    currentArtifact,
    imageFullscreen,
    onClose
}: ChartPatternAuditFullscreenModalProps) => {
    if (!imageFullscreen || !currentArtifact || artifactLoadFailed) {
        return null;
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={(event) => {
                if (event.key === 'Escape') {
                    onClose();
                }
            }}
        >
            <img
                alt="Full size chart"
                className="max-w-[95vw] max-h-[95vh] object-contain"
                crossOrigin="use-credentials"
                src={buildArtifactImageUrl(currentArtifact)}
            />
            <button className="absolute top-4 right-4 text-zinc-400 hover:text-white text-2xl" type="button" onClick={onClose}>
                ×
            </button>
        </div>
    );
};
